import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { Post, Category } from '../types';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PostModal from './PostModal';
import { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const PostsList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [manualCreatedAtFrom, setManualCreatedAtFrom] = useState('');
  const [manualCreatedAtTo, setManualCreatedAtTo] = useState('');
  const [scheduledAtFrom, setScheduledAtFrom] = useState('');
  const [scheduledAtTo, setScheduledAtTo] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: categoriesData } = useQuery(['categories', 'for-filter'], () => apiClient.getCategories({ limit: 100 }), { staleTime: 60000 });
  const queryClient = useQueryClient();
  const { admin } = useAuth();

  if (!hasPermission(admin, 'posts', 'read')) {
    return <div className="text-red-600">У вас нет прав для просмотра постов.</div>;
  }

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error, refetch } = useQuery(
    ['posts', page, debouncedSearch, statusFilter, typeFilter, categoryFilter, manualCreatedAtFrom, manualCreatedAtTo, scheduledAtFrom, scheduledAtTo],
    () => apiClient.getPosts({
      page,
      limit: 10,
      search: debouncedSearch,
      status: statusFilter || undefined,
      type: typeFilter || undefined,
      category_id: categoryFilter || undefined,
      manual_created_at_from: manualCreatedAtFrom || undefined,
      manual_created_at_to: manualCreatedAtTo || undefined,
      scheduled_at_from: scheduledAtFrom || undefined,
      scheduled_at_to: scheduledAtTo || undefined,
    })
  );

  const handleDelete = async (id: string) => {
    if (window.confirm('Вы уверены, что хотите удалить этот пост?')) {
      try {
        await apiClient.deletePost(id);
        toast.success('Пост удален успешно');
        queryClient.invalidateQueries(['posts']);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Ошибка удаления поста');
      }
    }
  };

  const handleCreatePost = () => {
    navigate('/dashboard/posts/edit/new');
  };

  const handleEditPost = (post: Post) => {
    navigate(`/dashboard/posts/edit/${post.id}`);
  };

  const handleSavePost = async (postData: Partial<Post>) => {
    try {
      await apiClient.createPost(postData as any);
      queryClient.invalidateQueries(['posts']);
    } catch (error: any) {
      throw error;
    }
  };

  if (isLoading) return <div>Загрузка...</div>;
  if (error) {
    const status = (error as AxiosError)?.response?.status;
    if (status === 403) {
      return <div className="text-red-600">У вас нет прав для просмотра постов или категорий. Обратитесь к администратору.</div>;
    }
    return <div>Ошибка загрузки постов</div>;
  }

  const posts = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Посты</h1>
        <button 
          onClick={handleCreatePost}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Создать пост
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            placeholder="Поиск постов..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 lg:flex-row">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[140px]"
          >
            <option value="">Все статусы</option>
            <option value="draft">Черновик</option>
            <option value="scheduled">Запланирован</option>
            <option value="published">Опубликован</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[140px]"
          >
            <option value="">Все типы</option>
            <option value="detailed">Детальный</option>
            <option value="texted">Текстовый</option>
            <option value="imaged">С изображением</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[140px]"
          >
            <option value="">Все категории</option>
            {categoriesData?.data.map((cat: Category) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={manualCreatedAtFrom}
            onChange={e => setManualCreatedAtFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Дата создания от"
          />
          <input
            type="date"
            value={manualCreatedAtTo}
            onChange={e => setManualCreatedAtTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Дата создания до"
          />
          <input
            type="datetime-local"
            value={scheduledAtFrom}
            onChange={e => setScheduledAtFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Публикация от"
          />
          <input
            type="datetime-local"
            value={scheduledAtTo}
            onChange={e => setScheduledAtTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Публикация до"
          />
          <button
            onClick={() => {
              setSearch('');
              setStatusFilter('');
              setTypeFilter('');
              setCategoryFilter('');
              setManualCreatedAtFrom('');
              setManualCreatedAtTo('');
              setScheduledAtFrom('');
              setScheduledAtTo('');
            }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 min-w-[100px]"
          >
            Сбросить
          </button>
        </div>
      </div>

      {/* Posts Table */}
      <div className="bg-white shadow rounded-lg">
        {/* Mobile view - cards */}
        <div className="md:hidden space-y-4 p-4">
          {posts.map((post: Post) => (
            <div key={post.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-sm font-medium text-gray-900 truncate flex-1 mr-2">
                  {post.title}
                </h3>
                <div className="flex space-x-2 flex-shrink-0">
                  <button 
                    onClick={() => handleEditPost(post)}
                    className="text-primary-600 hover:text-primary-900 p-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-xs text-gray-500">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    post.type === 'detailed' ? 'bg-blue-100 text-blue-800' :
                    post.type === 'texted' ? 'bg-green-100 text-green-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {post.type}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    post.status === 'published' ? 'bg-green-100 text-green-800' :
                    post.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {post.status}
                  </span>
                </div>
                <div className="text-xs">
                  <div className="font-medium text-gray-700">Автор:</div>
                  <div>{post.author.username}</div>
                </div>
                <div className="text-xs">
                  <div className="font-medium text-gray-700">Создан:</div>
                  <div>{new Date(post.created_at).toLocaleDateString()}</div>
                </div>
                {post.manual_created_at && (
                  <div className="text-xs">
                    <div className="font-medium text-gray-700">Дата создания:</div>
                    <div>{new Date(post.manual_created_at).toLocaleDateString()}</div>
                  </div>
                )}
                {post.scheduled_at && (
                  <div className="text-xs">
                    <div className="font-medium text-gray-700">Дата публикации:</div>
                    <div>{new Date(post.scheduled_at).toLocaleString()}</div>
                  </div>
                )}
                {post.event_location && (
                  <div className="text-xs">
                    <div className="font-medium text-gray-700">Место проведения:</div>
                    <div>{post.event_location}</div>
                  </div>
                )}
                {post.event_time && (
                  <div className="text-xs">
                    <div className="font-medium text-gray-700">Дата проведения:</div>
                    <div>{post.event_time}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Desktop view - table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Название
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Тип
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Автор
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата создания
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата публикации
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Место проведения
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата проведения
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map((post: Post) => (
                <tr key={post.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {post.title}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {post.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      post.status === 'published' ? 'bg-green-100 text-green-800' :
                      post.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {post.author.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {post.manual_created_at ? new Date(post.manual_created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {post.scheduled_at ? new Date(post.scheduled_at).toLocaleString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {post.event_location}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {post.event_time ? post.event_time : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditPost(post)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Предыдущая
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Следующая
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Показано <span className="font-medium">{((page - 1) * 10) + 1}</span> до{' '}
                  <span className="font-medium">
                    {Math.min(page * 10, pagination.total)}
                  </span>{' '}
                  из <span className="font-medium">{pagination.total}</span> результатов
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Предыдущая
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Следующая
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <PostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSavePost}
      />
    </div>
  );
};

export default PostsList; 